/* eslint-disable react/no-unstable-nested-components */
import { Card, Stack, Box } from "@mui/material";
import { gridStringOrNumberComparator, DataGrid } from "@mui/x-data-grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "utils/LayoutContainers/DashboardLayout";
import DashboardNavbar from "utils/Navbars/DashboardNavbar";
import CircularProgress from "@mui/material/CircularProgress";
import { getAPICall } from "lib/callApi";
import { SNAPSHOT } from "lib/constants";
import { enqueueSnackbar } from "notistack";
import "styles/snapshot.scss";

function isNullOrEmpty(str) {
  return str === null || str === "" || str === undefined;
}

function Snapshot() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [snapshotData, setSnapshotData] = useState({
    companyCount: 0,
    userCount: 0,
    kpiCount: 0,
    companies: [],
  });

  // Custom sorter than puts null/empty values at the bottom of the sort
  const nullHandlingSortComparator = (v1, v2, cellParams1, cellParams2) => {
    if (isNullOrEmpty(v1) || isNullOrEmpty(v2)) {
      if (v1 === v2) return 0;
      const sortModel = cellParams1.api.getSortModel();
      const sortColumn = sortModel.find((sm) => sm.field === cellParams1.field);
      if (sortColumn && sortColumn.sort === "desc") {
        return isNullOrEmpty(v1) ? -1 : 1;
      }
      return isNullOrEmpty(v1) ? 1 : -1;
    }
    return gridStringOrNumberComparator(v1, v2, cellParams1, cellParams2);
  };

  const RenderIntegrations = (props) => {
    const { value } = props;
    return (
      <Box
        sx={{
          display: "flex",
        }}
      >
        {Object.entries(value).map(
          ([integration, enabled]) =>
            enabled && (
              <Box
                sx={{ display: "flex", justifyContent: "flex-end", px: "0.5rem" }}
                key={integration}
              >
                <img
                  src={`/logos/${integration}.svg`}
                  alt={`${integration} logo`}
                  title={integration}
                  height={40}
                  width={40}
                />
              </Box>
            )
        )}
      </Box>
    );
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      headerClassName: "datatable-header",
      width: 50,
    },
    {
      field: "name",
      headerName: "Company Name",
      headerClassName: "datatable-header",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "startDate",
      headerName: "Start Date",
      headerClassName: "datatable-header",
      type: "date",
      // width: 150,
      valueGetter: (params) => {
        if (isNullOrEmpty(params)) return "";
        return new Date(params);
      },
    },
    {
      field: "subscriptionType",
      headerName: "Subscription Type",
      headerClassName: "datatable-header",
      width: 150,
      sortComparator: nullHandlingSortComparator,
    },
    {
      field: "subscriptionStatus",
      headerName: "Subscription Status",
      headerClassName: "datatable-header",
      width: 150,
      sortComparator: nullHandlingSortComparator,
    },
    {
      field: "subscriptionEndDate",
      headerName: "Subscription End Date",
      headerClassName: "datatable-header",
      width: 150,
      type: "date",
      sortComparator: nullHandlingSortComparator,
      valueGetter: (params) => (params.value ? new Date(params.value) : ""),
    },
    {
      field: "email",
      headerName: "Admin Email",
      headerClassName: "datatable-header",
      minWidth: 200,
    },
    {
      field: "userCount",
      headerName: "User Count",
      headerClassName: "datatable-header",
      type: "number",
      // width: 110,
    },
    {
      field: "subscriptionAmount",
      headerName: "Subscription Amount",
      headerClassName: "datatable-header",
      type: "number",
      width: 170,
      sortComparator: nullHandlingSortComparator,
      valueGetter: (params) => (params.value ? parseFloat(params.value, 0) : ""),
      valueFormatter: (params) => {
        if (isNullOrEmpty(params.value)) {
          return "";
        }
        return `$${params.value}`;
      },
    },
    {
      field: "lastActivity",
      headerName: "Last Activity",
      headerClassName: "datatable-header",
      width: 110,
      type: "date",
      sortComparator: nullHandlingSortComparator,
      valueGetter: (params) => (params.value ? new Date(params.value) : ""),
    },
    {
      field: "totalKPIs",
      headerName: "Total KPIs",
      headerClassName: "datatable-header",
      type: "number",
      valueFormatter: (params) =>
        params?.value?.toString?.()?.replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ","),
    },
    {
      field: "usersTrackingKPIs",
      headerName: "Users Tracking KPIs",
      headerClassName: "datatable-header",
      type: "number",
      width: 150,
    },
    {
      field: "usersOverTen",
      headerName: "Users Averaging 10+",
      headerClassName: "datatable-header",
      type: "number",
      width: 150,
    },
    {
      field: "competitionCount",
      headerName: "Competitions Run",
      headerClassName: "datatable-header",
      type: "number",
      width: 130,
    },
    {
      field: "coachupCount",
      headerName: "Coachup Reports",
      headerClassName: "datatable-header",
      type: "number",
      width: 130,
    },
    {
      field: "totalRevenue",
      headerName: "Total Revenue",
      headerClassName: "datatable-header",
      type: "number",
      width: 130,
      sortComparator: nullHandlingSortComparator,
      valueGetter: (params) => (params.value ? parseFloat(params.value, 0) : ""),
      valueFormatter: (params) => {
        if (isNullOrEmpty(params.value)) {
          return "";
        }
        return `$${params?.value?.toLocaleString?.()}`;
      },
    },
    {
      field: "companyIntegrations",
      headerName: "Integrations",
      headerClassName: "datatable-header",
      width: 250,
      renderCell: RenderIntegrations,
      sortable: false,
    },
  ];

  const getSnapshotData = async () => {
    setIsLoading(true);
    try {
      const result = await getAPICall(SNAPSHOT, true);
      if (result?.data?.success) {
        setSnapshotData(result?.data?.data || []);
      } else if (result?.response?.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: "Could not retrieve snapshot data!",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text:
            error?.response?.data?.message || error.message || "Could not retrieve snapshot data!",
        }),
        {
          variant: "custom",
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const userData = localStorage.getItem("user");

    if (!userData) {
      navigate("/sign-in");
    }

    getSnapshotData();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <div role="tabpanel" id={`simple-tabpanel-${0}`} aria-labelledby={`simple-tab-${0}`}>
        <MDBox sx={{ pt: 3 }}>
          <Card>
            <MDBox p={3} pb={0} lineHeight={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Alysio Snapshot
              </MDTypography>
              <MDBox sx={{ display: "inline-flex", float: "right" }}>
                <MDBox className="alysio-totals-group">
                  <p className="alysio-totals-label">Total Companies</p>
                  <p className="alysio-totals-value">{snapshotData.companyCount}</p>
                </MDBox>
                <MDBox className="alysio-totals-group">
                  <p className="alysio-totals-label">Total Users</p>
                  <p className="alysio-totals-value">{snapshotData.userCount}</p>
                </MDBox>
                <MDBox className="alysio-totals-group">
                  <p className="alysio-totals-label">Total KPIs Tracked</p>
                  <p className="alysio-totals-value">{snapshotData.kpiCount.toLocaleString()}</p>
                </MDBox>
              </MDBox>
            </MDBox>

            <MDBox
              sx={{
                width: "100%",
                overflowX: "auto",
                fontSize: "0.875rem",
              }}
            >
              {isLoading ? (
                <Stack alignItems="center">
                  <CircularProgress
                    sx={{
                      position: "fixed",
                      left: "50%",
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      zIndex: 2,
                    }}
                    color="info"
                    size="10vh"
                  />
                </Stack>
              ) : (
                ""
              )}
              <DataGrid
                sx={{
                  mt: "1rem",
                  "&.MuiTypography-root": {
                    fontSize: "1rem !important",
                  },
                }}
                rows={snapshotData.companies}
                columns={columns}
                initialState={{
                  pagination: {
                    paginationModel: {
                      pageSize: 10,
                    },
                  },
                }}
                pageSizeOptions={[10, 25, 50]}
                disableRowSelectionOnClick
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
                }
              />
            </MDBox>
          </Card>
        </MDBox>
      </div>
    </DashboardLayout>
  );
}

export default Snapshot;
